import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {NumberFormatter} from '@mintdata-wsp/common/controllers/spreadsheet/functions/util/text/NumberFormatter';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {TextUtil} from '@mintdata-wsp/common/util/TextUtil';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {IFormulaExpression, ISheetAPI, ISheetValue, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ADateTime} from '@mintdata/mint-kit/api/values/ADateTime';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {JSON_ITEM_FIELD_NAME} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';

export class TextFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'text';
    }

    @Function({
        title: 'formatNumber(number, format)',
        tip: 'Converts a number into text according to a specified format.',
        alias: 'FORMAT_NUMBER',
        example: 'FORMAT_NUMBER(1.234, "#.##")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'This function always returns text according to a specified format',
                type: Type.String
            }
    })
    @bind
    public formatNumber(@Param({
                            title: 'number',
                            tip: 'The number to format.',
                            type: SheetType.AnyIValue
                        }) number:IValue,
                        @Param({
                            title: 'format',
                            tip: 'The pattern by which to format the number, enclosed in quotation marks',
                            type: Type.String
                        }) format:AString):AString
    {
        const val = TextFunctions._value<ANumber>(number);
        format = TextFunctions._trim(TextFunctions._value<AString>(format));
        return AString.fromString(NumberFormatter.apply(val.value, format.value));
    }

    @Function({
        title: 'formatDate(date, format_string)',
        tip: 'Converts date/time in the first argument to the text' +
            ' with the format specified in second argument',
        alias: 'FORMAT_DATE',
        example: 'FORMAT_DATE(NOW(), "YYYY-MM-DD")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns formatted date time text string',
                type: Type.String
            }
    })
    @bind
    public formatDate(@Param({
                          title: 'date',
                          tip: 'The date to format, in Excel-style.',
                          type: Type.DateTime
                      }) date:ADateTime,
                      @Param({
                          title: 'format_string',
                          tip: 'The string with date format.',
                          type: Type.String
                      }) format:AString):AString
    {
        const val = date.toMoment().format(TextFunctions._trim(format).value);
        return AString.fromString(val);
    }

    private static _value<T extends IValue>(input:IValue):T
    {
        if( input.getType().isEqualTo(Type.Frame) )
        {
            const allValues = (input as IFrame).getAllValues();
            return allValues && allValues.length && (allValues[0] as T);
        }

        return input as T;
    }

    private static _trim(str:AString):AString
    {
        let val = str.value;
        if( val.startsWith('"') || val.startsWith('\'') )
        {
            val = val.replace('"', '');
            val = val.replace('\'', '');
        }

        if( val.endsWith('"') || val.endsWith('\'') )
        {
            val = val.replace('"', '');
            val = val.replace('\'', '');
        }

        return AString.fromString(val);
    }

    @Function({
        title: 'upper(text)',
        tip: 'Converts text to uppercase',
        alias: 'UPPER',
        example: 'UPPER("text")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns text in uppercase',
                type: Type.String
            }
    })
    @bind
    public upper(@Param({
        title: 'text',
        tip: 'Text to convert',
        type: Type.String
    }) text:AString):AString | AError
    {
        if( !text )
        {
            return new AError('No text provided');
        }
        const value = text.value;
        return new AString(value.toUpperCase());
    }

    @Function({
        title: 'TRIM(text)',
        tip: 'Trims whitespace characters from both ends of text.',
        alias: 'TRIM',
        example: 'TRIM("text")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns trimmed text',
                type: Type.String
            }
    })
    @bind
    public trim(@Param({
        title: 'text',
        tip: 'Text to trim',
        type: Type.String
    }) text:AString):AString | AError
    {
        if( !text )
        {
            return new AError('No text provided');
        }

        const value = text.value;
        return new AString(value.trim());
    }

    @Function({
        title: 'lower(text)',
        tip: 'Converts text to lower case',
        alias: 'LOWER',
        example: 'LOWER("text")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns text in lowercase',
                type: Type.String
            }
    })
    @bind
    public lower(@Param({
        title: 'text',
        tip: 'Text to convert',
        type: Type.String
    }) text:AString):AString | AError
    {
        if( !text )
        {
            return new AError('No text provided');
        }
        const value = text.value;
        return new AString(value.toLowerCase());
    }

    @Function({
        title: 'len(text)',
        tip: 'Returns the number of characters in a text string',
        alias: 'LEN',
        example: 'LEN("text")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Number of characters in a text string',
                type: Type.Integer
            }
    })
    @bind
    public len(@Param({
        title: 'text',
        tip: 'Text to process',
        type: Type.String
    }) text:AString):AInteger | AError
    {
        if( !text )
        {
            return new AError('No text provided');
        }
        const value = text.value;
        return new AInteger(value.length);
    }

    @Function({
        title: 'LEFT(string)',
        tip: 'Returns the first character or characters in a text string, based on the number of characters you specify',
        alias: 'LEFT',
        example: 'LEFT("text",2)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the first character or characters',
                type: Type.String
            }
    })
    @bind
    public left(@Param({
                    title: 'text',
                    tip: 'Text get characters from',
                    type: Type.String
                }) text:AString,
                @Param({
                    title: 'numOfChars',
                    tip: 'Number of characters to get',
                    type: Type.Integer,
                    optional: true
                }) numOfChars:AInteger):AString | AError
    {
        if( !text )
        {
            return EvalErrors.Value;
        }
        if( numOfChars && numOfChars.value < 0 )
        {
            return EvalErrors.Value;
        }
        const value = text.value;
        if( numOfChars && value.length <= numOfChars.value )
        {
            return text;
        }
        const result = numOfChars ? value.slice(0, numOfChars.value) : value.charAt(0);
        return new AString(result);
    }

    @Function({
        title: 'WRAP(string, fragment1 [, fragment2])',
        tip: 'Wraps the string with provided fragments.',
        alias: 'WRAP',
        example: 'WRAP("text", "<", ">")',
        icon: '',
        returns:
            {
                title: 'Resulting string',
                tip: 'Returns the wrapped string',
                type: Type.String
            }
    })
    @bind
    public wrap(@Param({
                    title: 'original',
                    tip: 'Original string',
                    type: Type.String
                }) original:AString,
                @Param({
                    title: 'fragment1',
                    tip: 'Fragment to wrap with on left hand size or on both sides',
                    type: Type.String,
                    optional: true
                }) fragment1:AString,
                @Param({
                    title: 'fragment2',
                    tip: 'Fragment to wrap with on right hand side',
                    type: Type.String,
                    optional: true
                }) fragment2:AString):AString | AError
    {
        const left = fragment1 ? fragment1.value : '"';
        const right = fragment2 ? fragment2.value : left;
        return new AString(left + original.value + right);
    }

    @Function({
        title: 'RIGHT(string)',
        tip: 'Returns the last character or characters in a text string, based on the number of characters you specify',
        alias: 'RIGHT',
        example: 'RIGHT("text",2)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the last character or characters',
                type: Type.String
            }
    })
    @bind
    public right(@Param({
                     title: 'text',
                     tip: 'Text get characters from',
                     type: Type.String
                 }) text:AString,
                 @Param({
                     title: 'numOfChars',
                     tip: 'Number of characters to get',
                     type: Type.Integer,
                     optional: true
                 }) numOfChars:AInteger):AString | AError
    {
        if( !text )
        {
            return EvalErrors.Value;
        }
        if( numOfChars && numOfChars.value < 0 )
        {
            return EvalErrors.Value;
        }
        const value = text.value;
        if( numOfChars && value.length <= numOfChars.value )
        {
            return text;
        }
        const result = numOfChars ? value.slice(value.length - numOfChars.value, value.length) : value.charAt(value.length - 1);
        return new AString(result);
    }

    @Function({
        title: 'CONCATENATE(string)',
        tip: 'Joins two or more text strings into one string',
        alias: 'CONCATENATE',
        example: 'CONCATENATE("text","another text")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the concatenated string',
                type: Type.String
            }
    })
    @bind
    public concatenate(@Param({
                           title: 'first string',
                           tip: 'Text to concatenate with',
                           type: Type.String
                       }) text:AString,
                       @Param({
                           title: 'texts',
                           tip: 'Text(s) to concatenate',
                           type: Type.String,
                           repeatable: true
                       }) texts:AString[]):AString | AError
    {
        if( !text )
        {
            return new AError('No text provided');
        }
        if( !texts )
        {
            return text;
        }
        let strings = texts.map((t) => t.value);
        let result = text.value.concat(...strings);
        return new AString(result);
    }

    @Function({
        title: 'JOIN(delimiter, frameOrRange1, [frameOrRange2, ...])',
        tip: 'Concatenates the elements of one or more frames-or-ranges using a specified separator.',
        alias: 'JOIN',
        example: 'JOIN("-",A1:A100)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the joined string',
                type: Type.String
            }
    })
    @bind
    public join(@Param({
                    title: 'Separator',
                    tip: 'Separator to join with',
                    type: Type.String
                }) separator:AString,
                @Param({
                    title: 'Frames',
                    tip: 'Frames to join',
                    type: Type.Frame,
                    repeatable: true
                }) frames:IFrame[]):AString | AError
    {
        return this.textjoin(separator, ABoolean.false(), frames);
    }

    @Function({
        title: 'TEXTJOIN(separator, ignoreEmpty, strings1, [, strings2, ...] )',
        tip: 'Joins strings using the separator. If ignoreEmpty is TRUE, ' +
        'empty cells selected in the text arguments won\'t be included in the result.',
        alias: 'TEXTJOIN',
        example: 'TEXTJOIN(" ", TRUE, "hello", "world")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the joined string',
                type: Type.String
            }
    })
    @bind
    public textjoin(@Param({
                        title: 'Separator',
                        tip: 'Separator to join with',
                        type: Type.String
                    }) separator:AString,
                    @Param({
                        title: 'Ignore empty',
                        tip: 'Ignore empty',
                        type: Type.Boolean
                    }) ignoreEmpty:ABoolean,
                    @Param({
                        title: 'Strings',
                        tip: 'Strings to join',
                        type: SheetType.AnyIValue,
                        repeatable: true
                    }) args:IValue[]):AString | AError
    {
        const stringsOrError = TextUtil.collectStringsFromIValues(args);
        if( Type.isInstanceOf(stringsOrError, Type.Error) )
            return stringsOrError as AError;

        let stringsToJoin = stringsOrError as Array<string>;
        if( ignoreEmpty.value )
            stringsToJoin = stringsToJoin.filter(s => s.length > 0);

        return new AString(stringsToJoin.join(separator.value));
    }

    @Function({
        title: 'SPLIT(text, delimiter, [splitByEach], [removeEmptyText])',
        tip: 'Divides text around a specified character or string, returns a frame with all the fragments.',
        alias: 'SPLIT',
        example: 'SPLIT("1,2,3", ",")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the concatenated string',
                type: Type.Frame
            }
    })
    @bind
    public split(@Param({
                     title: 'Text',
                     tip: 'The text to divide.',
                     type: Type.String
                 }) text:AString,
                 @Param({
                     title: 'Delimeter',
                     tip: 'The character or characters to use to split text. ' +
                     'By default, each character in delimiter is considered individually, e.g. if delimiter is "the", ' +
                     'then text is divided around the characters "t", "h", and "e". ' +
                     'Set splitByEach to FALSE to turn off this behavior.',
                     type: Type.String
                 }) delimeter:AString,
                 @Param({
                     title: 'splitByEach',
                     tip: 'Whether or not to divide text around each character contained in delimiter. TRUE by default',
                     type: Type.Boolean,
                     optional: true
                 }) splitByEach:ABoolean,
                 @Param({
                     title: 'removeEmptyText',
                     tip: 'Whether or not to remove empty text messages from the split results. ' +
                     'The default behavior is to treat consecutive delimiters as one (if TRUE). ' +
                     'If FALSE, empty cells are added between consecutive delimiters. TRUE by default',
                     type: Type.Boolean,
                     optional: true
                 }) removeEmptyText:ABoolean):IFrame
    {
        // this table shows regexp example, that needs to be created for all flags combinations
        //  each | remove | regexp example
        // --------------------------------
        //   0   |    0   |  /(?:<delim>)/
        //   0   |    1   |  /(?:<delim>)+/
        //   1   |    0   |  /[<delim>]/
        //   1   |    1   |  /[<delim>]+/

        let reString = TextUtil.escapeRegExp(delimeter.value);

        if( !splitByEach || splitByEach.value )
            reString = '[' + reString + ']';
        else
            reString = '(?:' + reString + ')';

        if( !removeEmptyText || removeEmptyText.value )
            reString = reString + '+';

        const split = text.value.split(new RegExp(reString));

        const fields = [new FrameField('parts', Type.String)];
        const data = split.map(part => [new AString(part)]);

        return Frames.fromFieldsAndData(fields, data);
    }

    @Function({
        title: 'CONCAT_ROWS(valueOrRef, [valueOrRef...])',
        tip: 'Concat records from multiple frames. Primitives are treated as single-valued frame with single column __item.',
        alias: 'CONCAT_ROWS',
        example: 'CONCAT_ROWS(A1, B1:B2, "constant", 42)',
        icon: '',
        returns:
            {
                title: 'result',
                tip: 'Returns the resulting frame',
                type: Type.Frame
            }
    })
    @bind
    public concatRows(@Param({
        title: 'values',
        tip: 'Values to concatenate',
        type: SheetType.AnyIValue,
        repeatable: true
    }) values:Array<IValue>):IFrame | AError
    {
        const frames = values.map(value => Type.isInstanceOf(value, Type.Frame)
            ? value as IFrame
            : Frames.fromFieldsAndData([new FrameField(JSON_ITEM_FIELD_NAME, value.getType())], [[value]])
        );

        const fieldsMap = {};
        const fields = [];
        frames.forEach(frame =>
        {
            frame.getFields().forEach(field =>
            {
                if( fieldsMap[field.name] === undefined )
                {
                    fieldsMap[field.name] = fields.length;
                    fields.push(field);
                }
            });
        });

        const data = [];
        frames.forEach(frame =>
        {
            let frameFields = frame.getFields();
            frame.getRecords().forEach(record =>
            {
                //create row with AEmpty values right away...
                const dataRow = Array(fields.length).fill(AEmpty.Value);

                //now set non-empty values, that we actually have in this frame
                record.getCells().forEach((cell, index) =>
                    dataRow[fieldsMap[frameFields[index].name]] = cell
                );

                data.push(dataRow);
            });
        });

        return Frames.fromFieldsAndData(fields, data);
    }

    @Function({
        title: 'SUBSTITUTE(searchText, searchFor, replaceWith, [occurrenceNumber])',
        tip: 'Replaces existing text with new text in a string.',
        alias: 'SUBSTITUTE',
        example: 'SUBSTITUTE(A2, "word", "Word", 2)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the resulting string',
                type: Type.String
            }
    })
    @bind
    public substitute(@Param({
                          title: 'searchText',
                          tip: ' The text within which to search and replace.',
                          type: Type.String
                      }) searchText:AString,
                      @Param({
                          title: 'searchFor',
                          tip: 'The string to search for within searchText.',
                          type: Type.String
                      }) searchFor:AString,
                      @Param({
                          title: 'replaceWith',
                          tip: 'The string that will replace searchFor.',
                          type: Type.String
                      }) replaceWith:AString,
                      @Param({
                          title: 'occurrenceNumber',
                          tip: 'The instance of searchFor within searchText to replace with replaceWith.' +
                          ' By default, all occurrences of searchFor are replaced;' +
                          ' however, if occurrenceNumber is specified, only the indicated instance of searchFor is replaced.',
                          type: Type.Integer,
                          optional: true
                      }) occurenceNumber:AInteger):AString | AError
    {
        const regExp = new RegExp(TextUtil.escapeRegExp(searchFor.value), 'g');

        let result;
        if( occurenceNumber == null )
        {
            result = searchText.value.replace(regExp, replaceWith.value);
        }
        else
        {
            //below is a trick to convert float-point number to integer https://stackoverflow.com/a/8388831
            let needOccurence = occurenceNumber.value | 0;
            if( needOccurence < 0 )
                return new AError('#INVALID_OCCURENCE_NUMBER');

            let currOccurence = 0;
            result = searchText.value.replace(regExp, (match) =>
            {
                currOccurence++;
                return currOccurence == needOccurence ? replaceWith.value : match;
            });
        }

        return new AString(result);
    }

    @Function({
        title: 'REPLACE(text, position, length, newText)',
        tip: 'Replaces part of a text string with a different text string.',
        alias: 'REPLACE',
        example: 'REPLACE(A2, "word", "Word", 2)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the resulting string',
                type: Type.String
            }
    })
    @bind
    public replace(@Param({
                       title: 'text',
                       tip: 'The text, a part of which will be replaced.',
                       type: Type.String
                   }) text:AString,
                   @Param({
                       title: 'position',
                       tip: 'The position where the replacement will begin (starting from 1).',
                       type: Type.Integer
                   }) position:AInteger,
                   @Param({
                       title: 'length',
                       tip: 'The number of characters in the text to be replaced.',
                       type: Type.Integer
                   }) length:AInteger,
                   @Param({
                       title: 'newText',
                       tip: 'The text which will be inserted into the original text.',
                       type: Type.String
                   }) newText:AString):AString | AError
    {
        //below is a trick to convert float-point number to integer https://stackoverflow.com/a/8388831
        const pos = (position.value | 0) - 1; //also convert pos to 0-based
        if( pos < 0 )
            return new AError('#INVALID_POSITION');

        const len = length.value | 0;
        if( len < 0 )
            return new AError('#INVALID_LENGTH');

        const str = text.value;
        const result = str.substr(0, pos) + newText.value + str.substring(pos + len);

        return new AString(result);
    }

    @Function({
        title: 'REGEXMATCH(string, regexString)',
        tip: 'Whether a piece of text matches a regular expression.',
        alias: 'REGEXMATCH',
        example: 'REGEXMATCH("text", "ab(.*)")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the boolean true if matched',
                type: Type.Boolean
            }
    })
    @bind
    public regexMatch(@Param({
                          title: 'text',
                          tip: 'The text to be tested against the regular expression.',
                          type: Type.String
                      }) text:AString,
                      @Param({
                          title: 'regexString',
                          tip: 'The regular expression to test the text against.',
                          type: Type.String,
                          optional: true
                      }) regexString:AString):ABoolean | AError
    {
        const regExp = this._createRegExp(regexString.value);
        if( Type.isInstanceOf(regExp, Type.Error) )
            return regExp as AError;

        return new ABoolean(
            !!text.value.match(regExp as RegExp)
        );
    }

    @Function({
        title: 'REGEXEXTRACT(string, regexString)',
        tip: 'Extracts matching substrings according to a regular expression.',
        alias: 'REGEXEXTRACT',
        example: 'REGEXEXTRACT("text", "ab(.*)")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns a single match, or a frame in case of multiple matches',
                type: Type.String
            }
    })
    @bind
    public regexExtract(@Param({
                            title: 'text',
                            tip: 'The input text.',
                            type: Type.String
                        }) text:AString,
                        @Param({
                            title: 'regexString',
                            tip: 'The first part of text that matches this expression will be returned.',
                            type: Type.String,
                            optional: true
                        }) regexString:AString):IValue | AError
    {

        const regExp = this._createRegExp(regexString.value);
        if( Type.isInstanceOf(regExp, Type.Error) )
            return regExp as AError;

        const match = text.value.match(regExp as RegExp);
        if( !match )
            return new AError('#NO_MATCH');

        const strings = match.map(group => group === undefined || group === null ? AEmpty.Value : new AString(group));

        // no capturing groups => return the whole matching string
        if( strings.length === 1 )
            return strings[0];

        // single capturing group => return the content of this first group
        if( strings.length === 2 )
            return strings[1];

        // multiple capturing groups => return IFrame
        const fields = [new FrameField('group', Type.String)];
        const data = strings.slice(1).map(s => [s]);

        return Frames.fromFieldsAndData(fields, data);
    }

    @Function({
        title: 'REGEXREPLACE(string, regexString, replacement)',
        tip: 'Replaces part of a text string with a different text string using regular expressions.',
        alias: 'REGEXREPLACE',
        example: 'REGEXREPLACE("abfff", "ab(...)", "cde")',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the resulting string',
                type: Type.String
            }
    })
    @bind
    public regexReplace(@Param({
                            title: 'text',
                            tip: 'The text, a part of which will be replaced.',
                            type: Type.String
                        }) text:AString,
                        @Param({
                            title: 'regexString',
                            tip: 'The regular expression. All matching instances in text will be replaced.',
                            type: Type.String,
                            optional: true
                        }) regexString:AString,
                        @Param({
                            title: 'replacement',
                            tip: 'The text which will be inserted into the original text.',
                            type: Type.String
                        }) replacement:AString):AString | AError
    {
        const regExp = this._createRegExp(regexString.value, 'g');
        if( Type.isInstanceOf(regExp, Type.Error) )
            return regExp as AError;

        return new AString(
            text.value.replace(regExp as RegExp, replacement.value)
        );
    }

    @Function({
        title: 'FIND_REPLACE(string, regexString, replacement)',
        tip: 'Replaces part of a text string with a different text string using a replacing expression.',
        alias: 'FIND_REPLACE',
        example: 'FIND_REPLACE(B7, ":([^\\s]+)", "thing_" & @m1)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the resulting string',
                type: Type.String
            }
    })
    @bind
    public async findReplace(@Param({
                                 title: 'api',
                                 tip: 'api',
                                 type: SheetType.SheetAPI
                             }) api:ISheetAPI,
                             @Param({
                                 title: 'text',
                                 tip: 'The text, a part of which will be replaced.',
                                 type: Type.String
                             }) text:AString,
                             @Param({
                                 title: 'regexString',
                                 tip: 'The regular expression. All matching instances in text will be replaced.',
                                 type: Type.String,
                                 optional: true
                             }) regexString:AString,
                             @Param({
                                 title: 'replacement',
                                 tip: 'The expression to compute a replacement string.',
                                 type: SheetType.FormulaExpression
                             }) replacement:IFormulaExpression):Promise<AString | AError>
    {
        const runReplaceFn = (replacerFn):AError | AString =>
        {
            const regExp = this._createRegExp(regexString.value, 'g');
            if( Type.isInstanceOf(regExp, Type.Error) )
                return regExp as AError;

            // note: not using arrow-function here to access proper `arguments`
            const result = text.value.replace(regExp as RegExp, function()
            {
                // get rid of last 2 args [.., offset, source]
                return replacerFn(Array.from(arguments).slice(0, arguments.length - 2));
            });

            return new AString(result);
        };

        // FIRST PASS: extract all replacement parameters, so we can process them async-ly
        const allReplacementArgs = [];
        const firstPassResult = runReplaceFn(replacerArgs =>
            allReplacementArgs.push(replacerArgs)
        );

        if( Type.isError(firstPassResult) )
            return firstPassResult as AError;

        const sheetContext = api.getSheetContext();
        const stringToNamedArgValue = (s) => s ? new AString(s) : AEmpty.Value;

        const replacements = [];
        for( const replacementArgs of allReplacementArgs )
        {
            const namedArguments = new Map<string, IValue | ISheetValue>();
            namedArguments.set('@m', stringToNamedArgValue(replacementArgs[0]));

            const groupCnt = replacementArgs.length - 1;
            for( let groupInd = 1; groupInd <= groupCnt; ++groupInd )
                namedArguments.set(`@m${groupInd}`, stringToNamedArgValue(replacementArgs[groupInd]));

            const evalValue:IValue | ISheetValue = await replacement.eval(namedArguments);
            const convertedValue = sheetContext.convertToIValue(evalValue);
            if( Array.isArray(convertedValue) )
                return new AError('#INVALID_REPLACEMENT');

            const coercedValue = CoercionUtils.coerce(convertedValue, Type.String, null);
            if( !Type.isInstanceOf(coercedValue, Type.String) )
                return new AError('#CANT_COERCE_REPLACEMENT_TO_STRING');

            replacements.push((coercedValue as AString).value);
        }

        // SECOND PASS: use evaluated replacements
        let replacementIndex = 0;
        return runReplaceFn(
            () => replacements[replacementIndex++]
        );
    }

    @Function({
        title: 'MID(string, startingAt, extractLength)',
        tip: 'Returns a segment of a text string',
        alias: 'MID',
        example: 'MID("text",2,3)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns the last character or characters',
                type: Type.String
            }
    })
    @bind
    public mid(@Param({
                   title: 'text',
                   tip: 'The text string to extract a segment from',
                   type: Type.String
               }) text:AString,
               @Param({
                   title: 'startingAt',
                   tip: 'The index from the left of text string from which to begin extracting. The first character in text string has the index 1',
                   type: Type.Integer
               }) startingAt:AInteger,
               @Param({
                   title: 'extractLength',
                   tip: 'The length of the segment to extract. If the end of text string is reached before extractLength characters are encountered, MID returns the characters from startingAt to the end of text string',
                   type: Type.Integer
               }) extractLength:AInteger):AString | AError
    {
        if( !text
            || (startingAt.isError || startingAt.value <= 0)
            || (extractLength.isError || extractLength.value < 0) )
        {
            return EvalErrors.Value;
        }

        const startingAtZeroBased:number = startingAt.value - 1;
        const result:string = text.value.substring(startingAtZeroBased, startingAtZeroBased + extractLength.value);

        return new AString(result);
    }

    @Function({
        title: 'CHAR(number)',
        tip: 'Get a character from a number',
        alias: 'CHAR',
        example: 'CHAR(13)',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns a single character specified by a number',
                type: Type.String
            }
    })
    @bind
    public char(@Param({
        title: 'code',
        tip: 'A number between 1 and 255',
        type: Type.Integer
    }) code:AInteger):AString | AError
    {
        if( !code || code.value < 0 || code.value > 255 )
            return EvalErrors.Value;

        return new AString(String.fromCharCode(code.value));
    }

    @Function({
        title: 'GUID()',
        tip: 'Generate new globally unique id',
        alias: 'GUID',
        example: 'GUID()',
        icon: '',
        returns:
            {
                title: 'true',
                tip: 'Returns generated GUID',
                type: Type.String
            }
    })
    @bind
    public guid():AString
    {
        return new AString(IdUtil.newGUID());
    }

    @Function({
        title: 'EMOJI(name)',
        tip: 'Returns an emoji if it was found by name, if not found, the empty string ""',
        alias: 'EMOJI',
        example: 'EMOJI(":smile:")',
        icon: '',
        returns:
            {
                title: 'emoji',
                tip: 'Returns an emoji',
                type: Type.String
            }
    })
    @bind
    public async emoji(@Param({
        title: 'name',
        tip: 'name',
        type: Type.String
    }) name:AString):Promise<AString | AError>
    {
        const emojiName = TextUtil.trimEnds(name.value, ':');
        const emoji = await commonControllers.assets.emojis.getEmojiByName(emojiName);
        return emoji ? new AString(emoji.char) : new AError('#EMOJI_NOT_FOUND');
    }

    @Function({
        title: 'EMOJIS()',
        tip: 'Returns all available emojis',
        alias: 'EMOJIS',
        example: 'EMOJIS()',
        icon: '',
        returns:
            {
                title: 'emojis',
                tip: 'Returns all emojis',
                type: Type.Frame
            }
    })
    @bind
    public async emojis():Promise<IFrame | AError>
    {
        const emojis = await commonControllers.assets.emojis.getEmojis();

        return this._emojisToFrame(emojis);
    }

    @Function({
        title: 'EMOJI_SEARCH(searchText)',
        tip: 'Searches emojis',
        alias: 'EMOJI_SEARCH',
        example: 'EMOJI_SEARCH("flag")',
        icon: '',
        returns:
            {
                title: 'emojis',
                tip: 'Returns found emojis',
                type: Type.Frame
            }
    })
    @bind
    public async searchEmojis(@Param({
        title: 'searchText',
        tip: 'searchText',
        type: Type.String
    }) searchText:AString):Promise<IFrame | AError>
    {
        const emojis = await commonControllers.assets.emojis.searchEmojis(searchText.value);

        return this._emojisToFrame(emojis);
    }

    @Function({
        title: 'IS_EMOJI(name)',
        tip: 'Returns TRUE if name can be identified as emoji',
        alias: 'IS_EMOJI',
        example: 'IS_EMOJI(":smile:")',
        icon: '',
        returns:
            {
                title: 'result',
                tip: 'Returns TRUE if name can be identified as emoji',
                type: Type.Boolean
            }
    })
    @bind
    public async isEmoji(@Param({
        title: 'name',
        tip: 'name',
        type: Type.String
    }) name:AString):Promise<ABoolean | AError>
    {
        const emojiName = TextUtil.trimEnds(name.value, ':');
        const emoji = await commonControllers.assets.emojis.getEmojiByName(emojiName);
        return ABoolean.fromBoolean(!!emoji);
    }

    @Function({
        title: 'TO_EMOJI(text)',
        tip: 'Replaces all :foo and :foo: emoji strings that map to an actual found emoji with the emoji char',
        alias: 'TO_EMOJI',
        example: 'TO_EMOJI("this is :fire and :cold")',
        icon: '',
        returns:
            {
                title: 'text',
                tip: 'Returns new text with replacements',
                type: Type.String
            }
    })
    @bind
    public async toEmoji(@Param({
        title: 'text',
        tip: 'text',
        type: Type.String
    }) text:AString):Promise<AString | AError>
    {
        const emojiByName = await commonControllers.assets.emojis.getEmojiByNameMap();

        //  ':' is included into allowed characters to simplify processing...
        const result = text.value.replace(/:([\w:_-]+)([^\w:_-]|$)/gm, (match:string, g1:string, g2:string) =>
        {
            if( g1.endsWith(':') )
                g1 = g1.substring(0, g1.length - 1);

            const emoji = emojiByName[g1];
            return (emoji ? emoji.char : match) + (g2 || '');
        });

        return new AString(result);
    }

    @Function({
        title: 'FROM_EMOJI(text)',
        tip: 'Replaces all emoji chars with their canonical name (:sample).\n',
        alias: 'FROM_EMOJI',
        example: 'FROM_EMOJI("this is 🔥")',
        icon: '',
        returns:
            {
                title: 'text',
                tip: 'Returns new text with replacements',
                type: Type.String
            }
    })
    @bind
    public async fromEmoji(@Param({
        title: 'text',
        tip: 'text',
        type: Type.String
    }) text:AString):Promise<AString | AError>
    {
        const rootTrie = await commonControllers.assets.emojis.getEmojiByGraphemeTrie();

        let result = '';

        // convert to array to properly split over codePoints
        const source = Array.from(text.value);
        for( let i = 0; i < source.length; ++i )
        {
            let child = rootTrie.findLongestEmoji(source, i);
            if( !child )
            {
                result += source[i];
                continue;
            }

            result += `:${child.emoji.name}`;
            i += child.depth - 1;
        }

        return new AString(result);
    }

    @Function({
        title: 'TEXT_TEMPLATE(template, paramValue1, [paramValue2, ...])',
        tip: 'Substitutes question marks in the provided string with param values. ' +
        'Parameters can be named: in that case, provide a frame with matching column names as a values argument.',
        alias: 'TEXT_TEMPLATE',
        example: 'TEXT_TEMPLATE("SELECT * FROM table WHERE string_field = \'?\' AND numeric_field > ?", "string value", {42})',
        icon: '',
        returns:
            {
                title: 'text',
                tip: 'Returns a new text based on the template fulfilled with parameter values',
                type: Type.String
            }
    })
    @bind
    public textTemplate(@Param({
                            title: 'template',
                            tip: 'The text template.',
                            type: Type.String
                        }) template:AString,
                        @Param({
                            title: 'paramValue',
                            tip: 'Param values.',
                            type: SheetType.AnyIValue,
                            repeatable: true
                        }) values:IValue[]):AString | AError
    {
        let result = template.value;
        if( !result.includes('?') )
            return new AString(result);

        let namedParamMap = {};
        let flatValues = [];

        for( const arg of values )
        {
            if( arg.isFrame && (arg as IFrame).getRowsCount() > 0 )
            {
                const frameArg = (arg as IFrame);
                for( const field of frameArg.getFieldNames() )
                {
                    if( field.match(/^column\d+$/) )
                    {
                        frameArg.getColumn(field).rows.forEach(value =>
                        {
                            const coerced = CoercionUtils.coerce(arg, Type.String, null);
                            if( !Type.isInstanceOf(coerced, Type.Error) )
                                flatValues.push(value.toString());
                        });
                    }
                    else
                    {
                        namedParamMap[field] = frameArg.getCell(field, 0);
                    }
                }
            }
            else
            {
                const coerced = CoercionUtils.coerce(arg, Type.String, null);
                if( Type.isInstanceOf(coerced, Type.Error) )
                    return new AError('#CANT_COERCE_VALUE_TO_STRING');

                flatValues.push((coerced as AString).value);
            }
        }

        const totalValues = Object.keys(namedParamMap).length + flatValues.length;
        const totalParams = template.value.split('?').length - 1;
        if( totalParams > totalValues )
        {
            return new AError('#NEED_MORE_VALUES');
        }

        const namedValues = result.match(/(\?\w+)/g);
        if( namedValues )
        {
            for( const namedValue of namedValues )
            {
                const namedValueNoQ = namedValue.replace(/^\?/, '');
                if( Object.keys(namedParamMap).indexOf(namedValueNoQ) === -1 )
                    return new AError(`#MISSING_ARG: ${namedValue}`);

                result = result.replace(namedValue, namedParamMap[namedValueNoQ]);
            }
        }

        let paramNumber = 0;
        try
        {
            result = result.replace(/\?/g, () =>
            {
                paramNumber++;
                if( flatValues.length === 0 )
                    throw new Error(`#MISSING_ARG: #${paramNumber}`);

                return flatValues.shift();
            });
        }
        catch( e )
        {
            return new AError(e.message);
        }

        return new AString(result);
    }

    private _createRegExp(regexString:string, flags:string = ''):RegExp | AError
    {
        try
        {
            return new RegExp(regexString, flags || '');
        }
        catch( e )
        {
            return new AError('#INVALID_REGULAR_EXPRESSION');
        }
    }

    private _emojisToFrame(emojis)
    {
        const fields = [
            new FrameField('name', Type.String),
            new FrameField('keywords', Type.Frame),
            new FrameField('category', Type.String),
            new FrameField('char', Type.String),
            new FrameField('code_points', Type.String)
        ];

        const data = [];
        emojis.map(({name, char, category, keywords}) =>
        {
            const kwFrame = Frames.fromFieldsAndData(
                [new FrameField('keyword', Type.String)],
                keywords.map(kw => [new AString(kw)])
            );

            const cpString = Array.from(char)
                .map((cp:string) =>
                    `\\u${cp.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')}`
                )
                .join('');

            data.push([
                new AString(name),
                kwFrame,
                new AString(category),
                new AString(char),
                new AString(cpString)
            ]);
        });

        return Frames.fromFieldsAndData(fields, data);
    }
}
